<template>
  <div class="comic-detail-page">
    <div class="container" v-if="loading">
      <div class="loading">加载中...</div>
    </div>

    <div class="container" v-else>
      <div class="comic-detail">
        <div class="comic-cover">
          <img :src="getComicCoverUrl()" :alt="comic.title" @error="handleImageError">
          <span class="comic-status" v-if="comic.is_paid === 1">付费漫画</span>
          <span class="comic-status free" v-else>免费漫画</span>
        </div>
        <div class="comic-info">
          <h1 class="comic-title">{{ comic.title }}</h1>
          <div class="age-rating-bar" v-if="ageRatingText">
            <span class="age-rating-badge" :class="ageRatingClass">{{ ageRatingText }}</span>
            <span class="age-rating-tip">{{ ageRatingTip }}</span>
          </div>
          <div class="comic-meta">
            <div class="meta-item">
              <span class="label">作者:</span>
              <span class="value">{{ comic.author }}</span>
            </div>
            <div class="meta-item">
              <span class="label">状态:</span>
              <span class="value">{{ comic.status_text }}</span>
            </div>
            <div class="meta-item">
              <span class="label">更新时间:</span>
              <span class="value">{{ formatDate(comic.updatetime) }}</span>
            </div>
          </div>
          
          <div class="comic-description">
            <h3>简介</h3>
            <p>{{ comic.description || '暂无简介' }}</p>
          </div>

          <div class="comic-price" v-if="comic.is_paid === 1">
            <div class="price-info">
              <span class="price-label">阅读权限:</span>
              <div class="price-summary">
                <span class="price-value">{{ readingAccessText }}</span>
                <small class="price-note">{{ readingAccessNote }}</small>
              </div>
            </div>
            
            <div class="purchase-options">
              <template v-if="comic.need_vip && !hasFreeChapters">
                <button class="vip-btn" @click="goToVip">开通VIP阅读</button>
              </template>
              <button class="read-btn" v-else @click="startReading">{{ comic.need_vip && hasFreeChapters ? '先看免费章节' : '开始阅读' }}</button>
              <button
                v-if="comic.need_vip && hasFreeChapters && hasVipChapters"
                class="vip-btn"
                @click="goToVip"
              >
                开通VIP解锁后续
              </button>
              <button class="favorite-btn" :class="{ active: isFavorite }" @click="toggleFavorite">{{ isFavorite ? '已收藏' : '收藏' }}</button>
              <button class="report-comic-btn" @click="openReport('comic', comic.id)">举报</button>
            </div>
          </div>
          <div class="comic-actions" v-else>
            <button class="read-btn" @click="startReading">开始阅读</button>
            <button class="favorite-btn" :class="{ active: isFavorite }" @click="toggleFavorite">{{ isFavorite ? '已收藏' : '收藏' }}</button>
            <button class="report-comic-btn" @click="openReport('comic', comic.id)">举报</button>
          </div>

          <div class="comic-tags">
            <span class="tag" v-if="comic.is_vip">VIP</span>
          </div>
        </div>
      </div>

      <div class="comic-chapters">
        <h2 class="section-title">章节列表 ({{ chapters.length }})</h2>
        <div class="chapter-list">
          <div 
            class="chapter-item" 
            v-for="chapter in chapters" 
            :key="chapter.id" 
            @click="readChapter(chapter)"
            :class="{ 'disabled': !chapter.can_read }"
          >
            <div class="chapter-body">
              <div class="chapter-name">{{ chapter.title }}</div>
              <div class="chapter-info">
                <span class="chapter-date">{{ formatDate(chapter.createtime) }}</span>
                <span class="chapter-status" v-if="chapter.need_pay && !chapter.can_read">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lock-icon">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  需VIP
                </span>
                <span class="chapter-status free" v-else-if="!chapter.need_pay">
                  免费
                </span>
                <span class="chapter-status member" v-else>
                  会员可读
                </span>
              </div>
            </div>
          </div>
          <div class="empty-tip" v-if="chapters.length === 0">
            暂无章节
          </div>
        </div>
      </div>

      <div class="comic-rankings">
        <div class="ranking-card">
          <h2 class="section-title">热榜单</h2>
          <div class="ranking-list">
            <div
              class="ranking-item"
              v-for="(item, index) in hotRankingComics"
              :key="`hot-${item.id}`"
              @click.stop="goToComic(item.id)"
            >
              <strong>{{ String(index + 1).padStart(2, '0') }}</strong>
              <img :src="getFullImageUrl(item.cover)" :alt="item.title" @error="handleImageError">
              <div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.author || '人气作品' }}</p>
              </div>
            </div>
            <div class="empty-tip" v-if="hotRankingComics.length === 0">暂无热榜数据</div>
          </div>
        </div>

        <div class="ranking-card">
          <h2 class="section-title">人气榜单</h2>
          <div class="ranking-list">
            <div
              class="ranking-item"
              v-for="(item, index) in popularityRankingComics"
              :key="`popular-${item.id}`"
              @click.stop="goToComic(item.id)"
            >
              <strong>{{ String(index + 1).padStart(2, '0') }}</strong>
              <img :src="getFullImageUrl(item.cover)" :alt="item.title" @error="handleImageError">
              <div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.description || item.author || '高人气漫画' }}</p>
              </div>
            </div>
            <div class="empty-tip" v-if="popularityRankingComics.length === 0">暂无人气榜数据</div>
          </div>
        </div>
      </div>

      <div class="comic-comments">
        <h2 class="section-title">漫画评论</h2>
        <div class="comment-form">
          <textarea
            :placeholder="youthCommentRestricted ? '青少年模式下不能发表评论' : '说点什么吧...'"
            v-model="commentContent"
            :disabled="youthCommentRestricted"
          ></textarea>
          <button @click="submitComment" :disabled="youthCommentRestricted">
            {{ youthCommentRestricted ? '青少年模式下不可评论' : '发表评论' }}
          </button>
        </div>
        <p v-if="youthCommentRestricted" class="comment-restriction">青少年模式下不能发表评论，请先完成成年人密码认证。</p>
        <div class="comment-list">
          <div class="empty-tip" v-if="topComments.length === 0">
            暂无评论，快来发表第一条评论吧
          </div>
          <template v-for="comment in topComments" :key="comment.id">
            <div class="comment-item">
              <div class="comment-user">
                <img :src="getCommentAvatar(comment)" :alt="comment.nickname" class="user-avatar">
                <div class="user-info">
                  <div class="user-name">{{ comment.nickname }}</div>
                  <div class="comment-time">{{ formatDate(comment.createtime) }}</div>
                </div>
                <div class="comment-actions" v-if="isMyComment(comment)">
                  <button class="delete-btn" @click="deleteComment(comment.id)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M3 6h18"></path>
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="comment-content">{{ comment.content }}</div>
              <div class="comment-ops">
                <button class="comment-like" :class="{ liked: comment.is_liked }" @click="toggleCommentLike(comment)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2"><path d="M7 10v12"></path><path d="M15 5.88L14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"></path></svg>
                  <span>{{ comment.likes > 0 ? comment.likes : '赞' }}</span>
                </button>
                <button class="comment-reply" @click="startReply(comment)">回复</button>
                <button class="comment-report" @click="openReport('comment', comment.id)">举报</button>
              </div>
              <div class="reply-box" v-if="replyTargetId === comment.id">
                <input v-model.trim="replyContent" type="text" :placeholder="'回复 ' + comment.nickname + '：'" maxlength="300" @keyup.enter="submitReply">
                <button class="reply-send" @click="submitReply">发送</button>
                <button class="reply-cancel" @click="cancelReply">取消</button>
              </div>
              <div class="reply-list" v-if="repliesOf(comment.id).length">
                <div class="reply-item" v-for="reply in repliesOf(comment.id)" :key="reply.id">
                  <div class="reply-copy">
                    <strong>{{ reply.nickname }}</strong>
                    <span v-if="reply.reply_to" class="reply-at">回复 @{{ reply.reply_to }}：</span>
                    <span class="reply-text">{{ reply.content }}</span>
                  </div>
                  <div class="reply-meta">
                    <span class="reply-time">{{ formatDate(reply.createtime) }}</span>
                    <button class="comment-like mini" :class="{ liked: reply.is_liked }" @click="toggleCommentLike(reply)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2"><path d="M7 10v12"></path><path d="M15 5.88L14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"></path></svg>
                      <span>{{ reply.likes > 0 ? reply.likes : '赞' }}</span>
                    </button>
                    <button class="comment-report mini" @click="openReport('comment', reply.id)">举报</button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

    </div>

    <div class="report-modal" v-if="reportVisible" @click.self="closeReport">
      <div class="report-panel">
        <button type="button" class="report-close" @click="closeReport">×</button>
        <span class="small-label">举报中心</span>
        <h2>举报{{ reportTypeText }}</h2>
        <p class="report-tip">请选择举报原因，我们会尽快核实处理。</p>
        <div class="report-reasons">
          <label v-for="item in reportReasons" :key="item" class="report-reason">
            <input type="radio" v-model="reportReason" :value="item">
            <span>{{ item }}</span>
          </label>
        </div>
        <textarea v-model.trim="reportContent" placeholder="补充说明（选填，最多300字）" maxlength="300" rows="3"></textarea>
        <div class="report-actions">
          <button type="button" class="primary-btn" @click="submitReport" :disabled="reportSubmitting">{{ reportSubmitting ? '提交中...' : '提交举报' }}</button>
          <button type="button" class="ghost-btn" @click="closeReport">取消</button>
        </div>
        <p class="report-message">{{ reportMessage }}</p>
      </div>
    </div>

    <div class="message-container" v-if="messageVisible">
      <div class="message-box" :class="messageType">
        {{ messageText }}
      </div>
    </div>

  </div>
</template>

<script>
import api from '../api/index'
import request from '../api/request'
import { useRouter, useRoute } from 'vue-router'
import comicPlaceholder from '@/assets/comic-placeholder.svg'
import {
  initYouthStore,
  isYouthCommunityRestricted,
  YOUTH_CODE_LOCKED,
  YOUTH_CODE_CONTENT,
  youthErrorText
} from '../utils/youthContent'

export default {
  name: 'ComicDetail',
  setup() {
    const router = useRouter()
    const route = useRoute()
    return { router, route }
  },
  data() {
    return {
      baseUrl: request.baseUrl,
      loading: true,
      placeholderImage: comicPlaceholder,
      comic: {},
      chapters: [], // 章节列表
      comments: [], // 评论列表
      hotRankingComics: [],
      popularityRankingComics: [],
      commentContent: '', // 评论内容
      comicInfo: {}, // 漫画信息
      // 收藏
      isFavorite: false,
      isFavoriteLoading: false,
      // 评论回复
      replyTargetId: 0,
      replyContent: '',
      // 举报
      reportVisible: false,
      reportType: 'comic',
      reportTargetId: 0,
      reportReason: '',
      reportContent: '',
      reportSubmitting: false,
      reportMessage: '',
      reportReasons: ['色情低俗', '暴力血腥', '侵权抄袭', '广告诈骗', '其他'],
      // 消息提示相关
      messageVisible: false,
      messageText: '',
      messageType: 'info',
      messageTimer: null
    }
  },
  computed: {
    youthCommentRestricted() {
      return isYouthCommunityRestricted()
    },
    ageRatingText() {
      const map = { 'all': '全龄', '8+': '8+', '12+': '12+', '16+': '16+' }
      return map[this.comic.age_rating] || ''
    },
    ageRatingClass() {
      const map = { 'all': 'age-all', '8+': 'age-8', '12+': 'age-12', '16+': 'age-16' }
      return map[this.comic.age_rating] || ''
    },
    ageRatingTip() {
      const map = {
        'all': '适龄提示：全龄段适宜，无不良内容',
        '8+': '适龄提示：8周岁以上适宜，建议在家长陪伴下阅读',
        '12+': '适龄提示：12周岁以上适宜，含少量冲突情节',
        '16+': '适龄提示：16周岁以上适宜，含成人向情节'
      }
      return map[this.comic.age_rating] || ''
    },
    hasFreeChapters() {
      return this.chapters.some(chapter => !this.isPaidChapter(chapter))
    },
    hasVipChapters() {
      if (!this.chapters.length) return this.comic.is_paid === 1
      return this.chapters.some(chapter => this.isPaidChapter(chapter))
    },
    readingAccessText() {
      if (this.hasFreeChapters && this.hasVipChapters) return '部分章节免费'
      if (this.hasVipChapters) return 'VIP章节'
      return '免费阅读'
    },
    readingAccessNote() {
      if (this.hasFreeChapters && this.hasVipChapters) return '前置章节可免费阅读，后续章节需 VIP 会员。'
      if (this.hasVipChapters) return '付费章节需开通 VIP 会员。'
      return '当前章节均可免费阅读。'
    },
    topComments() {
      return this.comments.filter(item => !item.parent_id)
    },
    reportTypeText() {
      const map = { comic: '漫画', comment: '评论', post: '帖子' }
      return map[this.reportType] || '内容'
    }
  },
  watch: {
    youthCommentRestricted(restricted) {
      if (restricted) {
        this.commentContent = ''
      }
    }
  },
  created() {
    initYouthStore()
    // 从路由参数获取漫画ID
    const comicId = this.route.params.id
    if (comicId) {
      this.fetchComicDetail(comicId)
      this.fetchChapters(comicId)
      this.fetchRankingComics(comicId)
      
      // 加载评论数据
      this.loadComments(comicId)
    } else {
      this.router.push('/')
    }
  },
  methods: {
    isPaidChapter(chapter) {
      const paidChapter = Number(this.comicInfo.paid_chapter)
      const chapterNumber = Number(chapter.chapter_number)

      if (this.comicInfo.is_paid && paidChapter > 0 && Number.isFinite(chapterNumber)) {
        return chapterNumber >= paidChapter
      }

      return !!chapter.need_pay
    },
    // 获取漫画详情
    async fetchComicDetail(id) {
      try {
        this.loading = true
        const res = await api.getComicDetail(id)
        if (res && res.code === YOUTH_CODE_LOCKED) {
          this.showMessage(res.msg || youthErrorText[YOUTH_CODE_LOCKED], 'info')
          this.router.replace('/youth-protection')
          return
        }
        if (res && res.code === YOUTH_CODE_CONTENT) {
          this.showMessage(res.msg || youthErrorText[YOUTH_CODE_CONTENT], 'info')
          this.router.replace('/category')
          return
        }
        if (res && res.code === 1 && res.data) {
          this.comic = res.data
          // 设置页面标题
          document.title = `${this.comic.title} - 道晗漫画平台`
          this.checkFavorite(id)
          
          // 保存漫画信息到localStorage，以便在个人中心显示评论时能获取到标题
          localStorage.setItem(`comic_info_${id}`, JSON.stringify({
            id: this.comic.id,
            title: this.comic.title,
            cover: this.comic.cover
          }))
        } else {
          this.showMessage('获取漫画详情失败', 'error')
        }
      } catch (error) {
        console.error('获取漫画详情异常:', error)
        this.showMessage('获取漫画详情失败', 'error')
      } finally {
        this.loading = false
      }
    },
    
    // 获取漫画章节列表
    async fetchChapters(comic_id) {
      try {
        const res = await api.getComicChapters(comic_id)
        if (res && res.code === 1 && res.data) {
          this.chapters = (res.data.list || []).map(chapter => ({
            ...chapter,
            preview: this.getFullImageUrl(chapter.preview || this.comic.cover || '')
          }))
          this.comicInfo = res.data.comic || {}
        } else {
          this.showMessage('获取章节列表失败', 'error')
        }
      } catch (error) {
        console.error('获取章节列表异常:', error)
        this.showMessage('获取章节列表失败', 'error')
      }
    },
    
    getComicCoverUrl() {
      return this.getFullImageUrl(this.comic.cover || '')
    },

    getFullImageUrl(path) {
      if (!path) return this.placeholderImage
      if (/^https?:\/\//i.test(path) || path.startsWith('data:') || path.startsWith('blob:')) return path
      if (path.startsWith('//')) return window.location.protocol + path
      if (path.startsWith('/')) return this.baseUrl + path
      return `${this.baseUrl}/${path}`
    },

    handleImageError(event) {
      const img = event && event.target
      if (!img || img.dataset.fallbackApplied) return
      img.dataset.fallbackApplied = '1'
      img.src = this.placeholderImage
    },

    // 获取热榜和人气榜漫画
    async fetchRankingComics(comic_id) {
      try {
        const res = await api.getComicList({ 
          page: 1, 
          page_size: 12,
          exclude_id: comic_id // 排除当前漫画
        })
        if (res && res.code === 1 && res.data) {
          const list = (res.data.list || []).map(item => {
            if (!item.id) {
              console.error('榜单漫画数据缺少id字段:', item);
              return { ...item, id: item.comic_id || `missing-${Date.now()}` };
            }
            return item;
          }).filter(item => item.id);

          // 青少年模式的内容过滤已由服务端完成，这里直接使用返回结果
          this.hotRankingComics = list.slice(0, 6)
          this.popularityRankingComics = [...list].reverse().slice(0, 6)
        } else {
          console.error('获取榜单失败:', res);
        }
      } catch (error) {
        console.error('获取榜单异常:', error);
      }
    },
    
    // 格式化日期
    formatDate(timestamp) {
      if (!timestamp) return '未知'
      const date = new Date(timestamp * 1000)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    },
    
    // 开始阅读
    startReading() {
      if (this.chapters.length > 0) {
        // 跳转到第一章
        this.readChapter(this.chapters[0])
      } else {
        this.showMessage('暂无章节可读', 'info')
      }
    },
    
    // 阅读指定章节
    readChapter(chapter) {
      // 检查章节是否可读
      if (!chapter.can_read) {
        if (chapter.need_pay) {
          this.showMessage('付费内容需开通VIP后观看', 'info')
          return
        } else {
          // 其他无法阅读的原因
          this.showMessage('暂时无法阅读此章节', 'info')
          return
        }
      }
      
      // 可以阅读，跳转到阅读页
      console.log('跳转到阅读页:', this.comic.id, chapter.id)
      this.router.push(`/read/${this.comic.id}/${chapter.id}`)
    },
    
    // 前往VIP页面
    goToVip() {
      // 检查登录状态
      const token = localStorage.getItem('token')
      if (!token) {
        this.router.push(`/login?redirect=/user?tab=vip`)
        return
      }
      
      this.router.push('/user?tab=vip')
    },
    
    // 加载评论数据（服务端）
    async loadComments(comicId) {
      try {
        const res = await api.getComicComments(comicId)
        if (res && res.code === 1 && res.data) {
          this.comments = res.data.list || []
        } else {
          this.comments = []
        }
      } catch (error) {
        console.error('加载评论失败:', error)
        this.comments = []
      }
    },
    
    // 提交评论（服务端）
    async submitComment() {
      if (this.youthCommentRestricted) {
        this.showMessage('青少年模式下不能发表评论，请先完成成年人密码认证。', 'info')
        return
      }
      // 检查登录状态
      const token = localStorage.getItem('token')
      if (!token) {
        this.router.push(`/login?redirect=/comic/${this.comic.id}`)
        return
      }
      
      // 检查评论内容
      if (!this.commentContent.trim()) {
        this.showMessage('请输入评论内容', 'info')
        return
      }

      try {
        const res = await api.submitComicComment({
          comic_id: this.comic.id,
          content: this.commentContent.trim()
        })
        if (res && res.code === 1 && res.data) {
          this.comments.unshift(res.data)
          this.commentContent = ''
          this.showMessage('评论成功', 'success')
        } else {
          this.showMessage((res && res.msg) || '评论失败', 'error')
        }
      } catch (error) {
        // 未实名（业务码1101）：引导前往实名认证
        if (error && error.businessCode === 1101) {
          this.showMessage('请先完成实名认证后再评论（个人中心 - 设置 - 实名认证）', 'info')
          setTimeout(() => {
            this.router.push('/user?tab=realname')
          }, 1200)
          return
        }
        this.showMessage((error && error.message) || '评论失败', 'error')
      }
    },

    // 删除评论（服务端）
    async deleteComment(commentId) {
      if (!window.confirm('确定要删除这条评论吗？')) return
      try {
        const res = await api.deleteComicComment(commentId)
        if (res && res.code === 1) {
          this.comments = this.comments.filter(comment => comment.id !== commentId)
          this.showMessage('评论已删除', 'success')
        } else {
          this.showMessage((res && res.msg) || '删除失败', 'error')
        }
      } catch (error) {
        this.showMessage((error && error.message) || '删除失败', 'error')
      }
    },
    
    // 检查是否是自己的评论（服务端返回 is_mine）
    isMyComment(comment) {
      return Boolean(comment && comment.is_mine)
    },

    // 评论头像：优先使用用户头像，为空时生成本地字母头像
    getCommentAvatar(comment) {
      if (comment && comment.avatar) {
        return this.getFullImageUrl(comment.avatar)
      }
      const name = (comment && comment.nickname) || '游'
      const color = (name.charCodeAt(0) || 0) % 360
      return `data:image/svg+xml;base64,${btoa(
        `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">` +
        `<rect fill="hsl(${color}, 70%, 75%)" x="0" y="0" width="100" height="100"></rect>` +
        `<text x="50" y="50" font-size="50" fill="#ffffff" text-anchor="middle" dominant-baseline="central">${name.charAt(0).toUpperCase()}</text>` +
        `</svg>`
      )}`
    },

    // ===== 收藏 =====
    async checkFavorite(comicId) {
      if (!localStorage.getItem('token')) return
      try {
        const res = await api.getFavoriteStatus(comicId)
        this.isFavorite = !!(res && res.code === 1 && res.data && res.data.is_favorite)
      } catch (error) {
        console.error('获取收藏状态失败:', error)
      }
    },
    async toggleFavorite() {
      if (!localStorage.getItem('token')) {
        this.router.push(`/login?redirect=/comic/${this.comic.id}`)
        return
      }
      if (this.isFavoriteLoading) return
      this.isFavoriteLoading = true
      try {
        const res = this.isFavorite
          ? await api.unfavoriteComic(this.comic.id)
          : await api.favoriteComic(this.comic.id)
        if (res && res.code === 1) {
          this.isFavorite = !this.isFavorite
          this.showMessage(this.isFavorite ? '收藏成功，可在个人中心查看' : '已取消收藏', 'success')
        } else {
          this.showMessage((res && res.msg) || '操作失败', 'error')
        }
      } catch (error) {
        this.showMessage((error && error.message) || '操作失败', 'error')
      } finally {
        this.isFavoriteLoading = false
      }
    },

    // ===== 评论点赞/回复 =====
    repliesOf(commentId) {
      return this.comments.filter(item => item.parent_id === commentId)
    },
    async toggleCommentLike(comment) {
      if (!localStorage.getItem('token')) {
        this.router.push(`/login?redirect=/comic/${this.comic.id}`)
        return
      }
      try {
        const res = await api.likeComment(comment.id)
        if (res && res.code === 1 && res.data) {
          comment.is_liked = res.data.liked
          comment.likes = res.data.likes
        }
      } catch (error) {
        this.showMessage((error && error.message) || '操作失败', 'error')
      }
    },
    startReply(comment) {
      if (!localStorage.getItem('token')) {
        this.router.push(`/login?redirect=/comic/${this.comic.id}`)
        return
      }
      this.replyTargetId = comment.id
      this.replyContent = ''
    },
    cancelReply() {
      this.replyTargetId = 0
      this.replyContent = ''
    },
    async submitReply() {
      if (!this.replyContent.trim()) {
        this.showMessage('请输入回复内容', 'info')
        return
      }
      try {
        const res = await api.submitComicComment({
          comic_id: this.comic.id,
          parent_id: this.replyTargetId,
          content: this.replyContent.trim()
        })
        if (res && res.code === 1 && res.data) {
          this.comments.push(res.data)
          this.cancelReply()
          this.showMessage('回复成功', 'success')
        } else {
          this.showMessage((res && res.msg) || '回复失败', 'error')
        }
      } catch (error) {
        this.showMessage((error && error.message) || '回复失败', 'error')
      }
    },

    // ===== 举报 =====
    openReport(type, targetId) {
      if (!localStorage.getItem('token')) {
        this.router.push(`/login?redirect=/comic/${this.comic.id}`)
        return
      }
      this.reportType = type
      this.reportTargetId = targetId
      this.reportReason = ''
      this.reportContent = ''
      this.reportMessage = ''
      this.reportVisible = true
    },
    closeReport() {
      this.reportVisible = false
    },
    async submitReport() {
      if (!this.reportReason) {
        this.reportMessage = '请选择举报原因'
        return
      }
      this.reportSubmitting = true
      this.reportMessage = ''
      try {
        const res = await api.submitReport({
          type: this.reportType,
          target_id: this.reportTargetId,
          reason: this.reportReason,
          content: this.reportContent
        })
        if (res && res.code === 1) {
          this.reportVisible = false
          this.showMessage('举报已提交，我们会尽快核实处理', 'success')
        } else {
          this.reportMessage = (res && res.msg) || '提交失败'
        }
      } catch (error) {
        this.reportMessage = (error && error.message) || '提交失败'
      } finally {
        this.reportSubmitting = false
      }
    },

    // 添加跳转到漫画详情页方法
    goToComic(comicId) {
      console.log('点击榜单漫画，跳转到漫画ID:', comicId)
      if (!comicId) {
        this.showMessage('无效的漫画ID', 'error')
        return
      }
      
      // 如果是同一个漫画，则重新加载页面数据
      if (comicId === this.comic.id) {
        this.showMessage('已经在当前漫画页面', 'info')
        return
      }
      
      // 跳转到不同的漫画页面
      this.$nextTick(() => {
        this.router.push(`/comic/${comicId}`)
      })
    },

    // 添加消息提示工具方法
    showMessage(text, type = 'info', duration = 3000) {
      // 清除之前的定时器
      if (this.messageTimer) {
        clearTimeout(this.messageTimer)
        this.messageTimer = null
      }
      
      // 显示消息
      this.messageText = text
      this.messageType = type
      this.messageVisible = true
      
      // 设置定时器自动关闭
      this.messageTimer = setTimeout(() => {
        this.messageVisible = false
      }, duration)
    }
  }
}
</script>

<style scoped>
.comic-detail-page {
  padding: 40px 0;
  background-color: #f8f9fa;
  min-height: calc(100vh - 70px);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 16px;
  color: #666;
}

.loading:before {
  content: '';
  width: 30px;
  height: 30px;
  margin-right: 10px;
  border: 3px solid rgba(255, 107, 107, 0.3);
  border-radius: 50%;
  border-top-color: #ff6b6b;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 漫画详情顶部 */
.comic-detail {
  display: flex;
  background-color: #fff;
  border-radius: 10px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
}

.comic-detail:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 120px;
  background: linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%);
  opacity: 0.1;
  z-index: 0;
}

.comic-cover {
  flex: 0 0 250px;
  height: 350px;
  margin-right: 30px;
  position: relative;
  z-index: 1;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s;
}

.comic-cover:hover {
  transform: translateY(-5px);
}

.comic-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.comic-status {
  position: absolute;
  top: 15px;
  right: 0;
  background-color: rgba(255, 107, 107, 0.9);
  color: #fff;
  font-size: 13px;
  font-weight: bold;
  padding: 5px 15px 5px 10px;
  border-radius: 2px 0 0 2px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.comic-status.free {
  background-color: rgba(76, 175, 80, 0.9);
}

.comic-info {
  flex: 1;
  position: relative;
  z-index: 1;
}

.comic-title {
  font-size: 28px;
  margin: 0 0 15px;
  color: #333;
  line-height: 1.3;
  position: relative;
  display: inline-block;
}

.comic-title:after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 0;
  width: 50px;
  height: 3px;
  background: linear-gradient(to right, #ff6b6b, #ff8e8e);
  border-radius: 3px;
}

.comic-meta {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.age-rating-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 16px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
}

.age-rating-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.age-rating-badge.age-all { background: #52c41a; }
.age-rating-badge.age-8 { background: #13c2c2; }
.age-rating-badge.age-12 { background: #faad14; }
.age-rating-badge.age-16 { background: #fa541c; }

.age-rating-tip {
  font-size: 13px;
  color: #666;
}

.meta-item {
  margin-right: 25px;
  margin-bottom: 10px;
}

.label {
  color: #888;
  font-size: 14px;
  margin-right: 5px;
}

.value {
  color: #333;
  font-weight: 500;
  font-size: 14px;
}

.comic-description {
  margin-bottom: 25px;
}

.comic-description h3 {
  font-size: 18px;
  margin: 0 0 10px;
  color: #333;
}

.comic-description p {
  margin: 0;
  color: #666;
  line-height: 1.8;
  font-size: 15px;
  white-space: pre-line;
  max-height: 130px;
  overflow-y: auto;
  padding-right: 10px;
}

.comic-price {
  background-color: #f9f9f9;
  padding: 15px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border-left: 3px solid #ff6b6b;
}

.price-info {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.price-label {
  font-size: 15px;
  color: #666;
  margin-right: 10px;
}

.price-value {
  font-size: 24px;
  font-weight: bold;
  color: #ff6b6b;
}

.purchase-options, .comic-actions {
  display: flex;
  gap: 15px;
}

.read-btn, .vip-btn {
  padding: 12px 25px;
  border-radius: 50px;
  font-size: 15px;
  font-weight: bold;
  cursor: pointer;
  border: none;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.read-btn:before {
  content: '';
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z'%3E%3C/path%3E%3Cpath d='M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z'%3E%3C/path%3E%3C/svg%3E");
  width: 16px;
  height: 16px;
  margin-right: 8px;
  display: inline-block;
  background-repeat: no-repeat;
  background-position: center;
}

.vip-btn:before {
  content: '';
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolygon points='12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2'%3E%3C/polygon%3E%3C/svg%3E");
  width: 16px;
  height: 16px;
  margin-right: 8px;
  display: inline-block;
  background-repeat: no-repeat;
  background-position: center;
}

.read-btn {
  background: linear-gradient(135deg, #4caf50, #8bc34a);
  color: #fff;
  box-shadow: 0 5px 15px rgba(76, 175, 80, 0.3);
}

.read-btn:hover {
  background: linear-gradient(135deg, #43a047, #7cb342);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.4);
}

.vip-btn {
  background: linear-gradient(135deg, #ffc107, #ff9800);
  color: #fff;
  box-shadow: 0 5px 15px rgba(255, 193, 7, 0.3);
}

.vip-btn:hover {
  background: linear-gradient(135deg, #ffb300, #fb8c00);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(255, 193, 7, 0.4);
}

.comic-tags {
  display: flex;
  flex-wrap: wrap;
  margin-top: 20px;
}

.tag {
  padding: 5px 12px;
  border-radius: 50px;
  font-size: 13px;
  margin-right: 10px;
  margin-bottom: 5px;
  font-weight: bold;
}

.tag:nth-child(1) {
  background-color: rgba(255, 193, 7, 0.15);
  color: #ff9800;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.tag:nth-child(2) {
  background-color: rgba(33, 150, 243, 0.15);
  color: #2196f3;
  border: 1px solid rgba(33, 150, 243, 0.3);
}

/* 章节列表 */
.comic-chapters {
  background-color: #fff;
  border-radius: 10px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.section-title {
  font-size: 20px;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  position: relative;
}

.section-title:before {
  content: '';
  width: 4px;
  height: 20px;
  background: linear-gradient(to bottom, #ff6b6b, #ff8e8e);
  display: inline-block;
  margin-right: 12px;
  border-radius: 2px;
}

.chapter-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.chapter-item {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid #eee;
}

.chapter-item:hover {
  background-color: #f0f0f0;
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.07);
}

.chapter-item.disabled {
  opacity: 0.8;
  position: relative;
}

.chapter-item.disabled:after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.chapter-name {
  font-size: 15px;
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  color: #333;
}

.chapter-info {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #999;
  align-items: center;
}

.chapter-date {
  font-size: 12px;
  color: #999;
}

.chapter-status {
  font-size: 13px;
  color: #ff6b6b;
  font-weight: bold;
  display: flex;
  align-items: center;
}

.chapter-status.free {
  color: #4caf50;
}

.chapter-status.member {
  color: #2196f3;
}

.lock-icon {
  margin-right: 5px;
}

/* 漫画评论 */
.comic-comments {
  background-color: #fff;
  border-radius: 10px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
}

.comment-form {
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
}

.comment-form textarea {
  width: 100%;
  height: 120px;
  padding: 15px;
  border: 1px solid #eee;
  border-radius: 8px;
  resize: none;
  margin-bottom: 15px;
  font-size: 15px;
  transition: all 0.3s;
}

.comment-form textarea:focus {
  border-color: #ff6b6b;
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
}

.comment-form textarea:disabled,
.comment-form button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.comment-restriction {
  margin: -16px 0 22px;
  color: #ffcc66;
  font-size: 14px;
  line-height: 1.6;
}

.comment-form button {
  align-self: flex-end;
  padding: 10px 25px;
  background: linear-gradient(135deg, #ff6b6b, #ff8e8e);
  color: #fff;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 5px 15px rgba(255, 107, 107, 0.3);
}

.comment-form button:hover {
  background: linear-gradient(135deg, #ff5252, #ff7676);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(255, 107, 107, 0.4);
}

.comment-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.comment-item {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 20px;
}

.comment-user {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.user-avatar {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid white;
}

.user-info {
  flex: 1;
}

.comment-actions {
  margin-left: auto;
}

.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #ccc;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-btn:hover {
  color: #ff6b6b;
  background-color: rgba(255, 107, 107, 0.1);
}

.user-name {
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
}

.comment-time {
  font-size: 12px;
  color: #999;
}

.comment-content {
  font-size: 15px;
  line-height: 1.7;
  color: #444;
  padding-left: 57px;
}

/* 榜单卡片历史样式占位 */
.related-comics {
  background-color: #fff;
  border-radius: 10px;
  padding: 25px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
}

.related-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
}

.related-item {
  cursor: pointer;
  transition: transform 0.3s;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  z-index: 1;
}

.related-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
}

.related-item:active {
  transform: translateY(-2px);
}

.related-cover {
  width: 100%;
  height: 240px;
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.related-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}

.related-item:hover .related-cover img {
  transform: scale(1.08);
}

.related-title {
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  color: #333;
  text-align: center;
}

/* 消息框 */
.message-container {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
}

.message-box {
  padding: 12px 25px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  text-align: center;
  animation: slide-down 0.3s ease-out;
}

@keyframes slide-down {
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.message-box.info {
  background-color: #e3f2fd;
  color: #2196f3;
  border-left: 3px solid #2196f3;
}

.message-box.success {
  background-color: #e8f5e9;
  color: #4caf50;
  border-left: 3px solid #4caf50;
}

.message-box.error {
  background-color: #ffebee;
  color: #f44336;
  border-left: 3px solid #f44336;
}

/* 空数据提示 */
.empty-tip {
  text-align: center;
  padding: 40px 0;
  color: #999;
  font-size: 15px;
  grid-column: 1 / -1;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin: 10px 0;
}

.empty-tip:before {
  content: "";
  display: block;
  width: 40px;
  height: 40px;
  margin: 0 auto 10px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='%23999999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'%3E%3C/path%3E%3Cpolyline points='14 2 14 8 20 8'%3E%3C/polyline%3E%3Cline x1='16' y1='13' x2='8' y2='13'%3E%3C/line%3E%3Cline x1='16' y1='17' x2='8' y2='17'%3E%3C/line%3E%3Cpolyline points='10 9 9 9 8 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
}

@media (max-width: 991px) {
  .comic-detail {
    flex-direction: column;
    padding: 20px;
  }
  
  .comic-cover {
    margin-bottom: 25px;
    margin-right: 0;
    align-self: center;
    height: 300px;
    max-width: 220px;
  }
  
  .chapter-list, .related-list {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
  
  .chapter-info {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .chapter-date {
    margin-bottom: 5px;
  }
  
  .comment-content {
    padding-left: 0;
  }
}

@media (max-width: 576px) {
  .comic-detail {
    padding: 15px;
  }
  
  .section-title, .comic-title {
    font-size: 18px;
  }
  
  .comic-cover {
    height: 280px;
    max-width: 190px;
  }
  
  .meta-item {
    margin-right: 15px;
  }
  
  .purchase-options, .comic-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .read-btn, .vip-btn {
    width: 100%;
  }
  
  .chapter-list, .related-list {
    grid-template-columns: 1fr 1fr;
  }
  
  .chapter-name, .related-title {
    font-size: 14px;
  }
  
  .comment-user {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .user-avatar {
    margin-bottom: 10px;
  }
}

/* 道晗漫画平台：详情页大布局重构 */
.comic-detail-page {
  padding: 28px 0 48px;
  background:
    radial-gradient(circle at 6% 0%, rgba(34, 211, 215, 0.14), transparent 28%),
    radial-gradient(circle at 95% 8%, rgba(23, 34, 63, 0.12), transparent 30%),
    linear-gradient(180deg, #f6fbff 0%, #eef5ff 100%);
}

.comic-detail-page .container {
  max-width: 1420px;
}

.comic-detail {
  display: grid;
  grid-template-columns: minmax(260px, 340px) minmax(0, 1fr);
  gap: 28px;
  align-items: stretch;
  padding: 28px;
  border: 1px solid rgba(23, 34, 63, 0.08);
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(235, 250, 250, 0.9)),
    radial-gradient(circle at 90% 16%, rgba(34, 211, 215, 0.16), transparent 24%);
  box-shadow: 0 26px 70px rgba(31, 48, 92, 0.12);
}

.comic-detail::before {
  height: 100%;
  background:
    linear-gradient(135deg, rgba(23, 34, 63, 0.08), transparent 42%),
    radial-gradient(circle at 82% 18%, rgba(34, 211, 215, 0.18), transparent 28%);
  opacity: 1;
}

.comic-cover {
  flex: unset;
  width: 100%;
  height: min(520px, 60vw);
  min-height: 380px;
  margin: 0;
  border-radius: 24px;
  box-shadow: 0 28px 58px rgba(23, 34, 63, 0.22);
}

.comic-status {
  top: 18px;
  right: 18px;
  border-radius: 999px;
  padding: 7px 14px;
  background: linear-gradient(135deg, #ff6a75, #ff9a57);
}

.comic-status.free {
  background: linear-gradient(135deg, #22c55e, #22d3d7);
}

.comic-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px 10px 8px 0;
}

.comic-title {
  display: block;
  margin-bottom: 22px;
  color: #17223f;
  font-size: clamp(30px, 4vw, 54px);
  font-weight: 900;
  letter-spacing: 0;
}

.comic-title::after {
  width: 78px;
  height: 6px;
  background: linear-gradient(135deg, #22d3d7, #17223f);
}

.comic-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 22px;
}

.meta-item {
  margin: 0;
  padding: 14px 16px;
  border: 1px solid rgba(23, 34, 63, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
}

.label {
  display: block;
  margin: 0 0 6px;
  color: #76829b;
  font-size: 12px;
}

.value {
  color: #17223f;
  font-size: 15px;
  font-weight: 800;
}

.comic-description {
  padding: 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(23, 34, 63, 0.08);
}

.comic-description h3 {
  color: #17223f;
  font-size: 18px;
}

.comic-description p {
  max-height: 150px;
  color: #53617f;
}

.comic-price {
  margin-top: 18px;
  padding: 18px;
  border: 0;
  border-radius: 22px;
  background: #fff;
  box-shadow: inset 5px 0 0 #22d3d7;
}

.price-value,
.chapter-status {
  color: #0f9fa7;
}

.price-info {
  align-items: flex-start;
  gap: 12px;
}

.price-summary {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.price-value {
  font-size: 22px;
  line-height: 1.2;
}

.price-note {
  color: #7683a1;
  font-size: 13px;
  line-height: 1.5;
}

.purchase-options,
.comic-actions {
  flex-wrap: wrap;
  align-items: center;
}

.purchase-options button,
.comic-actions button {
  white-space: nowrap;
}

.read-btn,
.vip-btn,
.comment-form button {
  min-height: 48px;
  border-radius: 999px;
  background: linear-gradient(135deg, #22d3d7, #17223f);
  box-shadow: 0 14px 28px rgba(34, 211, 215, 0.22);
}

.vip-btn {
  background: linear-gradient(135deg, #ffb020, #ff7a00);
}

.read-btn:hover,
.vip-btn:hover,
.comment-form button:hover {
  filter: brightness(1.04);
  transform: translateY(-2px);
}

.comic-chapters,
.comic-comments,
.related-comics {
  border: 1px solid rgba(23, 34, 63, 0.08);
  border-radius: 30px;
  padding: 28px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 22px 58px rgba(31, 48, 92, 0.08);
}

.section-title {
  color: #17223f;
  font-size: 26px;
  font-weight: 900;
}

.section-title::before {
  width: 6px;
  height: 28px;
  background: linear-gradient(180deg, #22d3d7, #17223f);
}

.chapter-list {
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.chapter-item {
  min-height: 94px;
  padding: 18px;
  border-radius: 18px;
  border: 1px solid rgba(23, 34, 63, 0.08);
  background: linear-gradient(180deg, #fff, #f7fbff);
}

.chapter-item:hover {
  background: #fff;
  box-shadow: 0 18px 36px rgba(31, 48, 92, 0.1);
}

.chapter-name {
  color: #17223f;
  font-size: 17px;
  font-weight: 800;
}

.chapter-status.free {
  color: #16a34a;
}

.comment-form textarea {
  border-radius: 18px;
  background: #f7fbff;
}

.comment-form textarea:focus {
  border-color: #22d3d7;
  box-shadow: 0 0 0 4px rgba(34, 211, 215, 0.12);
}

.related-list {
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
}

.related-item {
  border-radius: 18px;
  box-shadow: 0 16px 34px rgba(31, 48, 92, 0.1);
}

.related-cover {
  height: 230px;
  margin: 0;
  border-radius: 18px 18px 0 0;
}

.related-title {
  padding: 12px;
  color: #17223f;
  font-weight: 800;
}

@media (max-width: 991px) {
  .comic-detail {
    grid-template-columns: 1fr;
  }

  .comic-cover {
    max-width: 360px;
    height: 480px;
    justify-self: center;
  }

  .comic-meta {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .comic-detail,
  .comic-chapters,
  .comic-comments,
  .related-comics {
    border-radius: 22px;
    padding: 18px;
  }

  .comic-cover {
    height: 360px;
    max-width: 260px;
    min-height: 0;
  }

  .chapter-list,
  .related-list {
    grid-template-columns: 1fr;
  }
}
</style> 

<style scoped>
/* 深色漫画馆详情页覆盖 */
.comic-detail-page {
  color: #eef3ff;
  background:
    radial-gradient(circle at 12% 0%, rgba(255, 204, 102, 0.14), transparent 30%),
    radial-gradient(circle at 88% 10%, rgba(35, 211, 238, 0.1), transparent 28%),
    linear-gradient(180deg, rgba(7, 9, 18, 0.22), rgba(7, 9, 18, 0.72)) !important;
}

.comic-detail-page .container {
  max-width: 1500px;
}

.comic-detail,
.comic-chapters,
.comic-comments,
.comic-rankings,
.ranking-card {
  border-radius: 10px !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  background: linear-gradient(180deg, rgba(20, 24, 39, 0.96), rgba(10, 13, 24, 0.98)) !important;
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.34) !important;
}

.comic-detail::before {
  background:
    radial-gradient(circle at 78% 18%, rgba(255, 204, 102, 0.16), transparent 32%),
    linear-gradient(135deg, rgba(22, 27, 45, 0.7), rgba(7, 9, 18, 0.28)) !important;
}

.comic-cover {
  border-radius: 10px !important;
  box-shadow: 0 28px 60px rgba(0, 0, 0, 0.48) !important;
}

.comic-title,
.section-title,
.chapter-name,
.related-title,
.comment-author {
  color: #f8fbff !important;
}

.comic-description,
.meta-item,
.comment-content,
.related-author,
.chapter-time {
  color: #9faaca !important;
}

.meta-label,
.chapter-status.free {
  color: #23d3ee !important;
}

.start-reading,
.favorite-btn,
.submit-comment {
  color: #07101d !important;
  background: linear-gradient(135deg, #ffcc66, #23d3ee) !important;
  border: 0 !important;
  border-radius: 8px !important;
}

.chapter-item,
.comment-item,
.ranking-item {
  border-radius: 10px !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  background: rgba(255, 255, 255, 0.05) !important;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.28) !important;
}

.chapter-item:hover,
.ranking-item:hover {
  border-color: rgba(255, 204, 102, 0.34) !important;
}

.comment-form textarea {
  color: #eef3ff !important;
  background: rgba(5, 8, 16, 0.84) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  border-radius: 10px !important;
}

.chapter-list {
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr)) !important;
}

.chapter-item {
  min-height: 88px !important;
  padding: 16px 18px !important;
  overflow: visible;
  display: flex;
  align-items: center;
}

.chapter-body {
  width: 100%;
  min-width: 0;
  padding: 0 !important;
  display: grid;
  gap: 10px;
}

.comic-rankings {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;
  margin-bottom: 30px;
  padding: 0 !important;
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
}

.ranking-card {
  padding: 28px;
}

.ranking-list {
  display: grid;
  gap: 12px;
}

.ranking-item {
  display: grid;
  grid-template-columns: 44px 64px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  transition: transform 0.25s ease, border-color 0.25s ease;
}

.ranking-item:hover {
  transform: translateY(-3px);
}

.ranking-item strong {
  color: #ffcc66;
  font-size: 22px;
  font-weight: 900;
}

.ranking-item img {
  width: 64px;
  height: 82px;
  object-fit: cover;
  border-radius: 8px;
}

.ranking-item h3 {
  margin: 0 0 6px;
  color: #f8fbff;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ranking-item p {
  margin: 0;
  color: #9faaca;
  font-size: 13px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (max-width: 900px) {
  .comic-rankings {
    grid-template-columns: 1fr;
  }
}
</style>

<style scoped>
/* ===== 收藏/点赞/回复/举报 ===== */
.favorite-btn,
.report-comic-btn {
  min-height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-weight: 800;
  cursor: pointer;
}

.favorite-btn {
  color: #07101d;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
  border: 0;
}

.favorite-btn.active {
  background: rgba(255, 255, 255, 0.14);
  color: #ffcc66;
  border: 1px solid rgba(255, 204, 102, 0.4);
}

.report-comic-btn {
  color: #9faaca;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.16);
}

.report-comic-btn:hover {
  color: #ff8a80;
  border-color: rgba(255, 138, 128, 0.5);
}

.comment-ops {
  display: flex;
  gap: 18px;
  align-items: center;
  margin: 12px 0 0 56px;
}

.comment-like,
.comment-reply,
.comment-report {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  background: transparent;
  color: #9faaca;
  font-size: 13px;
  cursor: pointer;
  padding: 2px 4px;
}

.comment-like.liked {
  color: #23d3ee;
}

.comment-like:hover,
.comment-reply:hover {
  color: #f5f7ff;
}

.comment-report {
  margin-left: auto;
}

.comment-report:hover {
  color: #ff8a80;
}

.comment-like.mini,
.comment-report.mini {
  font-size: 12px;
}

.reply-box {
  display: flex;
  gap: 8px;
  margin: 12px 0 0 56px;
}

.reply-box input {
  flex: 1;
  min-width: 0;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #eef3ff;
  background: rgba(7, 10, 18, 0.82);
  outline: none;
}

.reply-send,
.reply-cancel {
  border: 0;
  border-radius: 8px;
  padding: 0 14px;
  font-weight: 800;
  cursor: pointer;
}

.reply-send {
  color: #07101d;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
}

.reply-cancel {
  color: #dfe7ff;
  background: rgba(255, 255, 255, 0.08);
}

.reply-list {
  display: grid;
  gap: 8px;
  margin: 14px 0 0 56px;
}

.reply-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px 16px;
  align-items: center;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 9px;
  font-size: 13px;
  background: rgba(255, 255, 255, 0.045);
}

.reply-copy {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 5px 8px;
  min-width: 0;
  line-height: 1.55;
}

.reply-item strong {
  color: #f5f7ff;
  font-weight: 800;
}

.reply-at {
  color: #ffcc66;
  white-space: nowrap;
}

.reply-text {
  color: #c6cfe4;
  overflow-wrap: anywhere;
}

.reply-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
}

.reply-time {
  color: #6b7695;
  font-size: 12px;
  margin: 0;
}

/* 评论层级：主评论负责内容，回复只作为缩进后的次级信息。 */
.comic-comments .comment-list {
  gap: 0;
}

.comic-comments .comment-item {
  position: relative;
  padding: 20px 4px 22px 0 !important;
  border: 0 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09) !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.comic-comments .comment-item:first-child {
  padding-top: 6px !important;
}

.comic-comments .comment-item:last-child {
  border-bottom: 0 !important;
  padding-bottom: 4px !important;
}

.comment-user {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  column-gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}

.comment-user .user-avatar {
  width: 44px;
  height: 44px;
  margin: 0;
  border: 1px solid rgba(255, 255, 255, 0.78);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
}

.comment-user .user-info {
  min-width: 0;
}

.comment-user .user-name {
  margin-bottom: 4px;
  color: #f5f7ff;
  font-size: 14px;
  font-weight: 800;
}

.comment-user .comment-time {
  color: #7683a1;
  font-size: 12px;
}

.comment-actions {
  margin: 0;
  align-self: start;
}

.comment-actions .delete-btn {
  width: 30px;
  height: 30px;
  padding: 6px;
  color: #7683a1;
}

.comment-actions .delete-btn:hover {
  color: #ff8a80;
  background: rgba(255, 138, 128, 0.1);
}

.comic-comments .comment-content {
  margin-left: 56px;
  padding-left: 0 !important;
  color: #dfe7ff !important;
  font-size: 15px;
  line-height: 1.75;
}

@media (max-width: 640px) {
  .comment-user {
    grid-template-columns: 40px minmax(0, 1fr) auto;
    column-gap: 10px;
  }

  .comment-user .user-avatar {
    width: 40px;
    height: 40px;
  }

  .comic-comments .comment-content,
  .comment-ops,
  .reply-box,
  .reply-list {
    margin-left: 0;
  }

  .reply-item {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .reply-meta {
    width: 100%;
  }

  .reply-meta .comment-report {
    margin-left: auto;
  }
}

/* 举报弹窗 */
.report-modal {
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: grid;
  place-items: center;
  padding: 18px;
  background: rgba(0, 0, 0, 0.68);
  backdrop-filter: blur(6px);
}

.report-panel {
  position: relative;
  width: min(440px, 100%);
  padding: 24px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: linear-gradient(180deg, rgba(20, 24, 39, 0.98), rgba(9, 12, 22, 0.98));
  box-shadow: 0 32px 86px rgba(0, 0, 0, 0.46);
}

.report-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  color: #dfe7ff;
  font-size: 20px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
}

.report-panel h2 {
  margin: 8px 0 6px;
  color: #f5f7ff;
}

.report-tip {
  color: #9faaca;
  font-size: 13px;
}

.report-reasons {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 14px 0;
}

.report-reason {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #dfe7ff;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.05);
}

.report-reason input {
  width: auto;
}

.report-panel textarea {
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #eef3ff;
  background: rgba(7, 10, 18, 0.82);
  outline: none;
  resize: vertical;
}

.report-actions {
  display: flex;
  gap: 12px;
  margin-top: 14px;
}

.report-actions .primary-btn {
  flex: 1;
  min-height: 42px;
  border: 0;
  border-radius: 8px;
  color: #07101d;
  font-weight: 900;
  cursor: pointer;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
}

.report-actions .ghost-btn {
  min-height: 42px;
  padding: 0 18px;
  border: 0;
  border-radius: 8px;
  color: #dfe7ff;
  font-weight: 900;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
}

.report-message {
  margin-top: 10px;
  color: #ff8a80;
  font-size: 13px;
}
</style>
